import merge from 'lodash.merge';
import { inject, injectable } from 'inversify';

import { PersonalShelf } from '../constants';
import SettingsService from '../services/SettingsService';
import ConfigService from '../services/ConfigService';
import { container, getModule } from '../modules/container';
import StorageService from '../services/StorageService';
import type { Config } from '../../types/config';
import type { CalculateIntegrationType } from '../../types/calculate-integration-type';
import { DETERMINE_ENTITLEMENT_TYPE, DETERMINE_INTEGRATION_TYPE, REGISTER_WITH_CONFIG } from '../modules/types';
import { useConfigStore } from '../stores/ConfigStore';
import { logDebug, logError } from '../logger';
import type { CalculateEntitlementType } from '../../types/calculate-entitlement-type';

import WatchHistoryController from './WatchHistoryController';
import FavoritesController from './FavoritesController';
import AccountController from './AccountController';
import AccessController from './AccessController';

@injectable()
export default class AppController {
  private readonly configService: ConfigService;
  private readonly settingsService: SettingsService;
  private readonly storageService: StorageService;

  constructor(
    @inject(ConfigService) configService: ConfigService,
    @inject(SettingsService) settingsService: SettingsService,
    @inject(StorageService) storageService: StorageService,
  ) {
    this.configService = configService;
    this.settingsService = settingsService;
    this.storageService = storageService;
  }

  loadAndValidateConfig = async (configSource: string | undefined) => {
    const configLocation = this.configService.formatSourceLocation(configSource);
    const defaultConfig = this.configService.getDefaultConfig();

    if (!configLocation) {
      useConfigStore.setState({ config: defaultConfig });
    }

    let config = await this.configService.loadConfig(configLocation);

    config.id = configSource || '';
    config.assets = config.assets || {};

    // make sure the banner always defaults to the JWP banner when not defined in the config
    if (!config.assets.banner) {
      config.assets.banner = defaultConfig.assets.banner;
    }

    // Store the logo right away and set css variables so the error page will be branded
    useConfigStore.setState((state) => merge({}, state, { config: { assets: { banner: config.assets.banner } } }));

    config = await this.configService.validateConfig(config);
    config = merge({}, defaultConfig, config);

    return config;
  };

  initializeApp = async (url: string, language: string) => {
    logDebug('AppController', 'Initializing app', { url });

    const settings = await this.settingsService.initialize();
    const configSource = await this.settingsService.getConfigSource(settings, url);
    const config = await this.loadAndValidateConfig(configSource);
    const integrationType = this.calculateIntegrationType(config);
    const entitlementType = this.calculateEntitlementType(config);

    // update the config store
    useConfigStore.setState({ config, loaded: true, integrationType, entitlementType });

    /**
     * VD Private line
     */
    if (container.isBound(REGISTER_WITH_CONFIG)) {
      await container.get<((config: Config) => Promise<void>) | undefined>(REGISTER_WITH_CONFIG)?.(config);
    }

    // we could add the configSource to the storage prefix, but this would cause a breaking change for end users
    // (since 'window.configId' isn't used anymore, all platforms currently use the same prefix)
    this.storageService.initialize('jwapp');

    // update settings in the config store
    useConfigStore.setState({ settings });

    // when an integration is set, we initialize the AccountController
    if (integrationType) {
      await getModule(AccountController).initialize(url);
    }

    // when the apiAccessBridgeUrl is set up in the .ini file, we initialize the AccessController
    if (settings?.apiAccessBridgeUrl) {
      await getModule(AccessController).initialize();
    }

    if (config.features?.continueWatchingList && config.content.some((el) => el.type === PersonalShelf.ContinueWatching)) {
      await getModule(WatchHistoryController).initialize(language);
    }

    if (config.features?.favoritesList && config.content.some((el) => el.type === PersonalShelf.Favorites)) {
      await getModule(FavoritesController).initialize(language);
    }

    return { config, settings, configSource };
  };

  calculateIntegrationType = (config: Config) => {
    const registerIntegrationTypes = container.getAll<CalculateIntegrationType>(DETERMINE_INTEGRATION_TYPE);

    const activatedIntegrationTypes = registerIntegrationTypes.reduce((previousValue, calculateIntegrationType) => {
      const integrationType = calculateIntegrationType(config);

      return integrationType ? [...previousValue, integrationType] : previousValue;
    }, [] as string[]);

    if (activatedIntegrationTypes.length > 1) {
      throw new Error(`Failed to initialize app, more than 1 integrations are enabled: ${activatedIntegrationTypes.join(', ')}`);
    }

    return activatedIntegrationTypes[0] || null;
  };

  getIntegrationType = (): string | null => {
    const configState = useConfigStore.getState();

    if (!configState.loaded) {
      logError('AppController', 'A call to `AppController#getIntegrationType()` was made before loading the config');

      return null;
    }

    return configState.integrationType;
  };

  calculateEntitlementType = (config: Config) => {
    const registerEntitlementTypes = container.getAll<CalculateEntitlementType>(DETERMINE_ENTITLEMENT_TYPE);

    const activatedEntitlementTypes = registerEntitlementTypes.reduce((previousValue, calculateEntitlementType) => {
      const integrationType = calculateEntitlementType(config);

      return integrationType ? [...previousValue, integrationType] : previousValue;
    }, [] as string[]);

    if (activatedEntitlementTypes.length > 1) {
      throw new Error(`Failed to initialize app, more than 1 entitlement types are enabled: ${activatedEntitlementTypes.join(', ')}`);
    }

    return activatedEntitlementTypes[0] || null;
  };

  getEntitlementType = (): string | null => {
    const configState = useConfigStore.getState();

    if (!configState.loaded) {
      logError('AppController', 'A call to `AppController#getEntitlementType()` was made before loading the config');

      return null;
    }

    return configState.entitlementType || null;
  };

  getApiAccessBridgeUrl = (): string | undefined => {
    const configState = useConfigStore.getState();

    if (!configState.loaded) {
      logError('AppController', 'A call to `AppController#getApiAccessBridgeUrl()` was made before loading the config');

      return undefined;
    }
    return configState.settings?.apiAccessBridgeUrl || undefined;
  };

  getContentItems = async ({ language, isLoggedIn, hasSubscription }: { language: string; isLoggedIn: boolean; hasSubscription: boolean }) => {
    const config = useConfigStore.getState().config;

    return this.configService.getContentItems({ config, language, isLoggedIn, hasSubscription });
  };

  getMenuItems = async ({ language, isLoggedIn, hasSubscription }: { language: string; isLoggedIn: boolean; hasSubscription: boolean }) => {
    const config = useConfigStore.getState().config;

    return this.configService.getMenuItems({ config, language, isLoggedIn, hasSubscription });
  };
}
